import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const esc = (s) =>
  String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

// Render an off-screen white A4-styled node to a multi-page A4 jsPDF document.
async function buildPdf(innerHtml) {
  const el = document.createElement("div");
  el.style.cssText =
    "position:fixed;left:-9999px;top:0;width:794px;background:#ffffff;color:#141414;" +
    "padding:56px 60px;font-family:Georgia,'Times New Roman',serif;line-height:1.5;box-sizing:border-box;";
  el.innerHTML = innerHtml;
  document.body.appendChild(el);
  try {
    const canvas = await html2canvas(el, { scale: 2, backgroundColor: "#ffffff", useCORS: true });
    const img = canvas.toDataURL("image/jpeg", 0.95);
    const pdf = new jsPDF({ unit: "pt", format: "a4" });
    const pw = pdf.internal.pageSize.getWidth();
    const ph = pdf.internal.pageSize.getHeight();
    const imgH = (canvas.height * pw) / canvas.width;
    let heightLeft = imgH;
    let position = 0;
    pdf.addImage(img, "JPEG", 0, position, pw, imgH);
    heightLeft -= ph;
    while (heightLeft > 0) {
      position -= ph;
      pdf.addPage();
      pdf.addImage(img, "JPEG", 0, position, pw, imgH);
      heightLeft -= ph;
    }
    return pdf;
  } finally {
    document.body.removeChild(el);
  }
}

// Build + download a PDF from printable HTML.
export async function downloadHtmlPdf(innerHtml, filename) {
  const pdf = await buildPdf(innerHtml);
  pdf.save(filename);
}

// Build a PDF and return its base64 (no data-uri prefix) — for sending to the backend.
export async function htmlToPdfBase64(innerHtml) {
  const pdf = await buildPdf(innerHtml);
  const uri = pdf.output("datauristring"); // data:application/pdf;filename=...;base64,XXXX
  return uri.substring(uri.indexOf(",") + 1);
}

const brandHeader = () =>
  `<div style="border-bottom:2px solid #b8860b;padding-bottom:12px;margin-bottom:26px;">
     <div style="font-size:22px;font-weight:bold;letter-spacing:1px;">ZATEPLENIA<span style="color:#b8860b;">.EU</span></div>
   </div>`;

const brandFooter = (company) =>
  `<div style="margin-top:34px;padding-top:14px;border-top:1px solid #ccc;font-size:11px;color:#666;">
     ${esc(company.name)} · ${esc(company.street)}, ${esc(company.city)} · IČO: ${esc(company.ico)} ·
     DIČ: ${esc(company.dic)} · IČ DPH: ${esc(company.icdph)} · ${esc(company.email)}
   </div>`;

// Build printable HTML from a structured legal document ({title,intro,sections:[{h,p,li}]}).
export function legalDocHtml(doc, company) {
  const body = (doc.sections || [])
    .map((s) => {
      const paras = (s.p || []).map((p) => `<p style="margin:0 0 8px;font-size:13px;">${esc(p)}</p>`).join("");
      const items = s.li
        ? `<ul style="margin:0 0 8px 18px;padding:0;font-size:13px;">${s.li.map((i) => `<li style="margin:0 0 4px;">${esc(i)}</li>`).join("")}</ul>`
        : "";
      return `<h2 style="font-size:15px;margin:18px 0 8px;">${esc(s.h)}</h2>${paras}${items}`;
    })
    .join("");
  return (
    brandHeader() +
    `<h1 style="font-size:24px;margin:0 0 12px;">${esc(doc.title)}</h1>` +
    `<p style="font-size:13px;color:#333;margin:0 0 18px;">${esc(doc.intro)}</p>` +
    body +
    brandFooter(company)
  );
}

// Build printable HTML for a filled insulation order (objednávka).
export function orderHtml(order, company) {
  const row = (k, v) =>
    `<tr><td style="padding:6px 10px;border:1px solid #ddd;width:42%;color:#555;">${esc(k)}</td>
         <td style="padding:6px 10px;border:1px solid #ddd;">${esc(v || "—")}</td></tr>`;
  const today = new Date().toLocaleDateString("sk-SK");
  return (
    brandHeader() +
    `<h1 style="font-size:24px;margin:0 0 4px;">Záväzná objednávka zateplenia</h1>` +
    `<p style="font-size:12px;color:#777;margin:0 0 18px;">Dátum: ${esc(today)}</p>` +
    `<h2 style="font-size:15px;margin:16px 0 8px;">Objednávateľ</h2>` +
    `<table style="width:100%;border-collapse:collapse;font-size:13px;">
        ${row("Meno / firma", order.name)}
        ${row("Adresa", order.customer_address)}
        ${row("E-mail", order.email)}
        ${row("Telefón", order.phone)}
     </table>` +
    `<h2 style="font-size:15px;margin:18px 0 8px;">Predmet objednávky</h2>` +
    `<table style="width:100%;border-collapse:collapse;font-size:13px;">
        ${row("Adresa objektu", order.object_address)}
        ${row("Typ objektu", order.object_type)}
        ${row("Plocha fasády (m²)", order.area)}
        ${row("Materiál", order.material)}
        ${row("Hrúbka izolácie (mm)", order.thickness)}
        ${row("Povrchová úprava (omietka)", order.plaster)}
        ${row("Požadovaný termín", order.term)}
        ${row("Poznámka", order.note)}
     </table>` +
    (order.price
      ? `<div style="margin:16px 0;padding:12px 16px;border:1px solid #b8860b;background:#fbf6e9">
           <div style="font-size:12px;color:#777">Orientačná cena z kalkulačky (s DPH)</div>
           <div style="font-size:22px;font-weight:bold;color:#b8860b">${Number(order.price).toLocaleString("sk-SK")} €${order.price_per_m2 ? ` <span style="font-size:12px;color:#777;font-weight:normal">(≈ ${Number(order.price_per_m2).toLocaleString("sk-SK")} €/m²)</span>` : ""}</div>
           <div style="font-size:11px;color:#999">Presnú cenu potvrdíme po bezplatnej obhliadke.</div>
         </div>`
      : "") +
    `<h2 style="font-size:15px;margin:18px 0 8px;">Zhotoviteľ</h2>` +
    `<p style="font-size:13px;margin:0 0 4px;">${esc(company.name)}, ${esc(company.street)}, ${esc(company.city)}</p>` +
    `<p style="font-size:13px;margin:0 0 14px;">IČO: ${esc(company.ico)} · DIČ: ${esc(company.dic)} · IČ DPH: ${esc(company.icdph)}</p>` +
    `<p style="font-size:12px;color:#555;margin:0 0 26px;">Táto objednávka je nezáväzným návrhom na uzavretie zmluvy o dielo. Konečná cena a termín budú potvrdené po obhliadke. Odoslaním/podpisom objednávateľ potvrdzuje záujem o realizáciu za orientačných podmienok.</p>` +
    `<table style="width:100%;font-size:13px;margin-top:20px;"><tr>
        <td style="width:50%;">V ................................ dňa ..............<br/><br/>........................................<br/>Objednávateľ</td>
        <td style="width:50%;">V ................................ dňa ..............<br/><br/>........................................<br/>Zhotoviteľ</td>
     </tr></table>`
  );
}
